import { createFileRoute, Link } from "@tanstack/react-router";
import { lazy, Suspense, useEffect, useState } from "react";
import { ChevronDown } from "lucide-react";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import blogBodyHairImg from "@/assets/blog/blog-body-hair-transplantation.webp";
import blogHarrisRoleImg from "@/assets/blog/blog-dr-james-harris-role.webp";
import blogPrivacyImg from "@/assets/blog/blog-privacy-discretion.webp";
import blogNoShaveImg from "@/assets/blog/blog-no-shave-fue-discreet.webp";
import { absoluteUrl } from "@/lib/absolute-url";

const QuoteForm = lazy(() =>
  import("@/components/site/QuoteForm").then((m) => ({ default: m.QuoteForm })),
);

const TITLE = "Considering Hair Restoration? | RESTORE® Hair";
const DESCRIPTION =
  "Learn what to expect at a hair transplant consultation, how much a hair transplant costs, and get answers to the most common FUE hair restoration questions.";
const OG_IMAGE = "/img/r/wp-2022-09-result-athlete-deion.webp";
const CANONICAL = "https://restorehair.com/considering-hair-restoration/";

const YOUTUBE_SHORTS = "https://www.youtube.com/@restore_hair/shorts";
const HERO_IMAGE =
  "https://assets.restorehair.com/wp/2025/03/considering-hair-restoration-banner-img-new.webp";
const TYPED_WORDS = ["Hair Restoration?", "Hair Replacement?", "Hair Transplants?"];

const videos = [
  {
    img: "/img/r/wp-2022-09-result-athlete-deion.webp",
    alt: "Deion Sanders and RESTORE Hair",
    caption: "See Why the Legendary Deion Sanders Chose RESTORE®",
    href: "https://www.youtube.com/watch?v=Wjik2Uet5VQ",
  },
  {
    img: "/img/r/wp-2021-12-procedure-video-harris-v2.webp",
    alt: "RESTORE Medical Director Dr. James Harris",
    caption: "Meet RESTORE's Medical Director Dr. James Harris",
    href: "https://www.youtube.com/watch?v=CA5bJTwWBX0",
  },
  {
    img: "/img/r/wp-2023-10-deion-dr-harris-thumb.webp",
    alt: "Deion Sanders and Dr. James Harris",
    caption: "See Deion Sanders go head to head with our own Dr. James Harris",
    href: "https://www.youtube.com/watch?v=ehY4ZZ9UA68",
  },
  {
    img: "/img/r/wp-2022-09-brian-urlachervideo-bg.webp",
    alt: "Brian Urlacher and RESTORE Hair",
    caption: "Watch all our YouTube shorts, including many FAQs",
    href: YOUTUBE_SHORTS,
  },
];

const posts = [
  {
    slug: "how-does-body-hair-transplantation-work-and-when-is-it-used",
    title: "How does body hair transplantation work, and when is it used?",
    img: blogBodyHairImg,
  },
  {
    slug: "what-is-dr-james-harriss-role-at-restore",
    title: "What is Dr. James Harris's role at RESTORE Hair and why is he notable?",
    img: blogHarrisRoleImg,
  },
  {
    slug: "how-to-maintain-privacy-and-discretion-throughout-the-hair-restoration-process",
    title: "How to maintain privacy and discretion throughout the hair restoration process?",
    img: blogPrivacyImg,
  },
  {
    slug: "how-discreet-is-a-restore-hair-no-shave-fue-hair-transplant-for-working-professionals",
    title:
      "How discreet is a RESTORE Hair No-Shave FUE hair transplant for working professionals?",
    img: blogNoShaveImg,
  },
];

const faqs: { q: string; a: string[] }[] = [
  {
    q: "Is an FUE hair transplant permanent?",
    a: [
      "Hair transplants offer lasting results that are natural and virtually undetectable. The procedure involves transplanting individual hair follicles from a donor area to areas with thinning or no hair. These transplanted follicles continue to grow naturally, providing long-lasting and natural-looking results.",
      "Hair transplants are considered long-lasting when patients diligently care for their transplanted hair and use hair loss medications to ensure the grafted hair remains healthy and vital. FUE (Follicular Unit Extraction) hair transplants will remain on your head for as long as they would have lasted in the donor area from where they came from.",
      "The longevity of transplant results is largely attributed to the resilience of the transplanted follicles. With proper care, including regular maintenance and a healthy lifestyle, medical treatments for hair loss, individuals can expect their transplanted hair to endure for an extended period.",
      "FUE transplants are renowned for their effectiveness and minimal scarring, making it a popular choice for those seeking a permanent hair loss treatment. The transplanted hair retains its characteristics and durability, ensuring a lasting solution to restore both confidence and a fuller head of hair.",
    ],
  },
  {
    q: "Is the FUE hair transplant procedure painful?",
    a: [
      "An FUE hair transplant surgery is generally low on the pain scale.",
      "A transplant procedure is minimally invasive, and local anesthesia is used. Local anesthesia is administered to numb the donor and recipient areas, ensuring minimal discomfort during follicle extraction and transplantation.",
      "If pain is indeed reported by a patient, it is typically minor and well-tolerated, and can be mitigated by administering a little more local anesthesia. It is fair to compare hair transplant pain to your trips to the dentist — you may feel some odd sensations, but feeling genuine pain is very atypical and is rectified quickly with additional anesthesia. Sensations are often described as pressure rather than sharp pain.",
      "Postoperatively, patients may experience mild soreness or swelling, manageable with prescribed pain medication. Individual pain tolerance and anxiety levels influence perception, but advancements in FUE techniques prioritize patient comfort.",
      "Overall, the procedure is designed to be minimally invasive, making a hair transplant a well-tolerated option for effective hair restoration.",
    ],
  },
  {
    q: "Will I be awake during the procedure?",
    a: [
      "Yes. You will be awake throughout the procedure, making hair transplants a very safe and easy outpatient procedure.",
      "We have a television in the operating room, so you can spend the afternoon watching shows or listening to music. During the surgery, you will be under the effects of local anesthesia, and will not feel any pain.",
      "The awake state allows communication with the surgical team, ensuring a personalized and comfortable hair restoration experience. If you opt in for an oral sedative you may fall asleep during portions of the procedure.",
    ],
  },
  {
    q: "How long does the FUE hair transplant procedure take?",
    a: [
      "A hair transplant usually takes about 4 to 6 hours, sometimes longer depending on the amount of grafts.",
      "On operation day, you'll typically arrive right when the office opens to begin some brief pre-op paperwork. You'll then settle in and begin the numbing process. The transplant surgery starts shortly thereafter and lasts throughout the morning and into the early afternoon.",
      "Bigger transplants that move lots of follicles/grafts obviously take a bit longer than smaller transplants.",
      "During the surgery, we build in time for meals, snacks, and breaks for both yourself and the surgeon.",
    ],
  },
  {
    q: "What is the recovery / down time for an FUE hair transplant?",
    a: [
      "Recovery time after a Follicular Unit Extraction (FUE) hair transplant is relatively short. You will almost certainly want to rest a bit immediately after returning home from the surgery, and possibly for another day or so.",
      "You could theoretically go back to work the next day, and some men do, but most patients opt to take a few days off before returning to work. The donor and recipient area may have minor redness and swelling that are generally healed and back to normal within 5-7 days.",
      "Patients can usually resume regular activities within a few days but should avoid strenuous exercise for a few weeks.",
      "Complete recovery and visible hair growth may take several months, emphasizing the need for patience.",
      "Adherence to post operative care instructions, including gentle hair washing and avoiding certain activities, is crucial for optimal and successful FUE hair transplant outcomes.",
    ],
  },
  {
    q: "When will I see results?",
    a: [
      "Growth after hair transplantation varies from patient to patient.",
      "Some patients begin seeing new hair growth results between six and nine months after surgery. In rare cases, it can take a little longer.",
      "The initial growth is very exciting, but it is typically not indicative of the full and final results of the transplant hair. For some men, it takes 9 to 15 months post-op for the new hair to fully grow in and for the final cosmetic result to be reached.",
      "Patience is crucial, as it can take up to 18 months, providing a comprehensive and long-lasting solution to hair loss.",
      "Regular follow-ups with the surgeon during this period ensure proper monitoring and address any concerns in the journey towards restored hair density.",
    ],
  },
  {
    q: "How much does a hair transplant cost in my city?",
    a: [
      "We know everyone wants a direct answer to this question — but it's nearly impossible to answer this accurately without seeing and assessing your head first.",
      "Hair restoration costs vary widely by city and clinic reputation.",
      "The cost of a hair transplant in your city primarily depends on three factors: 1) your current level of hair loss, 2) your own personal restoration goals, which can range from aggressive to conservative, and 3) the quality, reputation, and experience of the doctor/surgeon.",
      "An accurate cost for a transplant can be assessed during your free consultation.",
    ],
  },
  {
    q: "Does the RESTORE® No-Shave FUE™ method really work?",
    a: [
      "Yes! The RESTORE Hair No-Shave FUE hair transplant procedure offers a groundbreaking solution, allowing patients to undergo hair restoration without the need to shave their entire head. Individual hair grafts are moved from the back of your head where you are plentiful to the balding areas.",
      "During the initial consultation at RESTORE Hair, experienced professionals evaluate the feasibility of this innovative approach based on individual hair loss patterns and goals. Patients considering this type of transplant can expect a seamless experience with the skilled team at RESTORE Hair.",
      "The No-Shave FUE hair transplant not only preserves the natural appearance of the existing hair but also delivers the transformative results synonymous with traditional FUE transplants.",
      "In essence, the no-shave FUE hair transplant at RESTORE Hair stands as a testament to the clinic's dedication to innovation and patient satisfaction. The procedure's success lies not only in its technical efficiency but also in the comprehensive support provided during the consultation and throughout the transformative journey, reaffirming RESTORE Hair's position as a leader in innovative and patient-focused hair restoration.",
    ],
  },
  {
    q: "What is the best age for a hair transplant?",
    a: [
      "The optimal age for a hair transplant depends on the progression of hair loss and if you have a receding hairline or balding on the crown.",
      "Older men are considered slightly better candidates for transplants than very young ones. This is because it's beneficial for the surgeon to fully visualize and understand your genetic balding pattern, which oftentimes hasn't materialized yet for men in their early or mid 20s.",
      "For slightly older men, the surgeon can properly plan a transplant that best suits your genetic balding pattern, and will suit you better as you age. This ensures the most positive result for the long-term. Early stages may benefit from non-surgical treatments, while more extensive cases might require a transplant for lasting results.",
      "Consulting with specialists helps determine the best timing, considering factors like donor hair availability and overall health. Choosing the right age ensures effective and long-lasting hair loss treatment.",
    ],
  },
  {
    q: "What hair loss treatments does RESTORE offer?",
    a: [
      "RESTORE Hair, a leading provider in hair loss treatment, offers comprehensive solutions to address various forms of hair loss. Our specialized services encompass both surgical and non-surgical approaches, providing tailored options for patients seeking effective interventions.",
      "For patients considering surgical intervention, RESTORE Hair specializes in our cutting-edge procedure, the No-Shave Follicular Unit Extraction (FUE) hair transplant.",
      "FUE involves the meticulous extraction of individual hair follicles from donor areas and hair implantation into thinning areas, ensuring a natural-looking result with minimal scarring. This surgical expertise allows for the precise transplantation of healthy hair follicles to areas affected by thinning or balding.",
      "Patients at RESTORE Hair benefit not only from cutting-edge treatments but also from a patient-centric ethos that prioritizes individualized care. From non-surgical options, like laser caps, over the counter topicals, and prescription medications for effective hair loss solutions, to specialized surgical procedures, RESTORE Hair is a leading destination for those seeking effective and personalized hair loss treatment solutions.",
    ],
  },
  {
    q: "What is the Norwood Scale to diagnose my hair loss?",
    a: [
      "The Norwood Scale, a widely used classification system for male pattern baldness, categorizes hair loss stages from I to VII. It assesses receding hairlines, temple recession, and crown balding. This visual aid helps professionals diagnose and plan treatments, offering a standardized way to communicate the extent of male pattern baldness and the number of grafts that may be required during your transplant.",
    ],
  },
  {
    q: "What can I expect at my free hair consultation with RESTORE?",
    a: [
      "At a free hair consultation with RESTORE Hair, patients can expect a personalized and informative experience in their journey toward effective hair restoration. The session begins with a thorough examination of the patient's hair loss condition by experienced professionals. The consultation encompasses a discussion about various hair restoration options, including cutting-edge techniques such as hair transplants.",
      "The knowledgeable team at RESTORE Hair addresses individual concerns and goals, providing detailed insights into the available transplant procedures. For those considering a hair transplant, such as the No-Shave Follicular Unit Extraction (FUE), the consultation delves into the intricacies of the process. Patients gain a comprehensive understanding of how FUE transplants work, from the extraction of donor hair to the meticulous transplantation process.",
      "During the consultation, the emphasis is not only on addressing current hair loss but also on planning for long-term results. The team discusses potential outcomes, timelines for recovery, and the importance of postoperative care. Patients receive tailored advice on the most suitable approach based on their unique circumstances and expectations.",
      "RESTORE Hair prioritizes transparency and patient education, ensuring that individuals leave the consultation with a clear understanding of the chosen hair loss treatment. The team also addresses any questions or concerns, fostering a sense of confidence and trust.",
      "Beyond the technical aspects, the free consultation at RESTORE Hair recognizes the emotional impact of hair loss. The team provides empathetic support, acknowledging the psychological aspects of the journey. Patients leave the consultation armed with knowledge, a personalized plan, and the assurance that they are in capable hands for their hair restoration needs.",
    ],
  },
];

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a.join(" ") },
  })),
};

export const Route = createFileRoute("/considering-hair-restoration")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "website" },
      { property: "og:url", content: CANONICAL },
      { property: "og:image", content: absoluteUrl(OG_IMAGE) },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:image", content: absoluteUrl(OG_IMAGE) },
    ],
    links: [{ rel: "canonical", href: CANONICAL }],
    scripts: [{ type: "application/ld+json", children: JSON.stringify(faqSchema) }],
  }),
  component: ConsideringHairRestorationPage,
});

function TypedHeadline() {
  const [wordIndex, setWordIndex] = useState(0);
  const [text, setText] = useState("");
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const word = TYPED_WORDS[wordIndex];
    if (!deleting && text === word) {
      const hold = window.setTimeout(() => setDeleting(true), 1600);
      return () => window.clearTimeout(hold);
    }
    if (deleting && text === "") {
      setDeleting(false);
      setWordIndex((i) => (i + 1) % TYPED_WORDS.length);
      return;
    }
    const tick = window.setTimeout(
      () =>
        setText((prev) =>
          deleting ? word.slice(0, prev.length - 1) : word.slice(0, prev.length + 1),
        ),
      deleting ? 45 : 90,
    );
    return () => window.clearTimeout(tick);
  }, [text, deleting, wordIndex]);

  return (
    <span className="text-primary">
      {text}
      <span aria-hidden="true" className="ml-0.5 animate-pulse">
        |
      </span>
    </span>
  );
}

function ConsideringHairRestorationPage() {
  const [open, setOpen] = useState<number | null>(null);

  return (
    <div className="min-h-screen bg-background font-sans">
      <Header />
      <main>
        <section className="relative">
          <img
            src={HERO_IMAGE}
            alt="RESTORE Hair patient considering hair restoration"
            className="h-[320px] w-full object-cover object-center md:h-[460px] lg:h-[520px]"
          />
          <div className="inset-0 flex items-center md:absolute">
            <div className="mx-auto w-full max-w-6xl px-4 lg:px-8">
              <div className="-mt-16 mb-10 max-w-lg bg-background p-6 shadow-lg md:mt-0 md:mb-0 md:p-8">
                <h1 className="text-3xl font-extrabold tracking-tight text-foreground md:text-4xl">
                  Considering <br className="hidden sm:block" />
                  <TypedHeadline />
                </h1>
              </div>
            </div>
          </div>
        </section>

        <section className="py-12 md:py-16">
          <div className="mx-auto max-w-4xl px-4 lg:px-8">
            <div>
              <h2 className="text-2xl font-extrabold text-foreground md:text-3xl">
                What to Expect at a Hair Transplant Consultation
              </h2>
              <p className="mt-4 text-base leading-relaxed text-muted-foreground">
                At your complimentary hair and scalp analysis we will answer the specific questions
                you may have. You'll learn about all of the treatment options offered by RESTORE®.
                You'll see first hand how the{" "}
                <Link to="/no-shave-fue-hair-transplant/" className="text-primary underline">
                  RESTORE® No-Shave FUE™ Procedure
                </Link>{" "}
                is performed and you will leave the meeting with a clear understanding of how this
                revolutionary new procedure will regrow your hair.
              </p>
              <Link
                to="/what-to-expect-at-your-hair-restoration-consultation/"
                className="mt-6 inline-block bg-primary px-8 py-4 text-sm font-bold uppercase tracking-wide text-primary-foreground transition hover:opacity-90"
              >
                What to Expect at a Hair Transplant Consultation
              </Link>
            </div>
          </div>
        </section>

        <section className="pb-12 md:pb-16">
          <div className="mx-auto max-w-4xl px-4 lg:px-8">
            <div>
              <h2 className="text-2xl font-extrabold text-foreground md:text-3xl">
                Hair Transplant Cost
              </h2>
              <p className="mt-4 text-base leading-relaxed text-muted-foreground">
                We care about delivering the best result that is custom-tailored to your needs. The
                exact cost of your hair restoration will be determined by the following: your hair
                loss, remaining donor hair, hair characteristics, and desired results.
              </p>
              <Link
                to="/pricing/"
                className="mt-6 inline-block bg-primary px-8 py-4 text-sm font-bold uppercase tracking-wide text-primary-foreground transition hover:opacity-90"
              >
                How Much Does a Hair Transplant Cost?
              </Link>
            </div>
          </div>
        </section>

        <section className="bg-muted py-12 md:py-16">
          <div className="mx-auto max-w-6xl px-4 lg:px-8">
            <h2 className="text-center text-3xl font-extrabold text-foreground md:text-4xl">
              Videos
            </h2>
            <div className="mx-auto mt-10 grid max-w-3xl gap-8 sm:grid-cols-2">
              {videos.map((v) => (
                <a
                  key={v.img}
                  href={v.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group block"
                >
                  <div className="aspect-video w-full overflow-hidden bg-foreground/5">
                    <img
                      src={v.img}
                      alt={v.alt}
                      loading="lazy"
                      className="h-full w-full object-cover transition group-hover:opacity-90"
                    />
                  </div>
                  <p className="mt-3 text-sm font-semibold leading-snug text-foreground">
                    {v.caption}
                  </p>
                </a>
              ))}
            </div>
            <div className="mt-10 text-center">
              <a
                href={YOUTUBE_SHORTS}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block bg-primary px-8 py-4 text-sm font-bold uppercase tracking-wide text-primary-foreground transition hover:opacity-90"
              >
                See All Hair Transplant Videos
              </a>
            </div>
          </div>
        </section>

        <section className="py-12 md:py-16">
          <div className="mx-auto max-w-6xl px-4 lg:px-8">
            <h2 className="text-center text-3xl font-extrabold text-foreground md:text-4xl">
              Blog
            </h2>
            <ul className="mt-10 grid gap-x-10 gap-y-8 sm:grid-cols-2">
              {posts.map((p) => (
                <li key={p.slug}>
                  <Link
                    to="/$slug/"
                    params={{ slug: p.slug }}
                    className="group grid grid-cols-[45%_1fr] items-center gap-5"
                  >
                    <div className="aspect-video w-full overflow-hidden">
                      <img
                        src={p.img}
                        alt={p.title}
                        loading="lazy"
                        className="h-full w-full object-cover transition group-hover:opacity-90"
                      />
                    </div>
                    <p className="text-base font-semibold leading-snug text-foreground transition group-hover:text-primary">
                      {p.title}
                    </p>
                  </Link>
                </li>
              ))}
            </ul>
            <div className="mt-8 text-center">
              <Link
                to="/hair-loss-blog/"
                className="inline-block bg-primary px-8 py-4 text-sm font-bold uppercase tracking-wide text-primary-foreground transition hover:opacity-90"
              >
                Read More Posts
              </Link>
            </div>
          </div>
        </section>

        <section className="bg-muted py-12 md:py-16">
          <div className="mx-auto max-w-4xl px-4 lg:px-8">
            <h2 className="text-center text-3xl font-extrabold text-foreground md:text-4xl">
              FAQ's
            </h2>
            <div className="mt-8 space-y-3">
              {faqs.map((f, i) => (
                <div key={f.q} className="border border-border bg-card">
                  <button
                    type="button"
                    onClick={() => setOpen(open === i ? null : i)}
                    aria-expanded={open === i}
                    className="flex w-full items-center justify-between gap-4 bg-card px-5 py-5 text-left"
                  >
                    <span className="text-base font-bold text-foreground md:text-lg">
                      {i + 1}. {f.q}
                    </span>
                    <ChevronDown
                      className={`h-5 w-5 shrink-0 text-primary transition-transform ${open === i ? "rotate-180" : ""}`}
                    />
                  </button>
                  {open === i && (
                    <div className="bg-card px-5 pb-6">
                      {f.a.map((para, k) => (
                        <p key={k} className="mt-3 text-base leading-relaxed text-muted-foreground">
                          {para}
                        </p>
                      ))}
                      {f.q.includes("Norwood") && (
                        <a href="/what-is-the-norwood-scale/" className="mt-5 block">
                          <img
                            src="/img/r/wp-2021-11-norwood-chart-1.webp"
                            alt="Norwood Scale hair loss chart"
                            loading="lazy"
                            className="h-auto w-full"
                          />
                        </a>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
            <div className="mt-8 text-center">
              <Link
                to="/hair-transplant-faqs/"
                className="inline-block bg-primary px-8 py-4 text-sm font-bold uppercase tracking-wide text-primary-foreground transition hover:opacity-90"
              >
                View More FAQ's
              </Link>
            </div>
          </div>
        </section>

        <Suspense fallback={null}>
          <QuoteForm />
        </Suspense>
      </main>
      <Footer />
    </div>
  );
}